import classnames from 'classnames';
import qs from 'qs';
import { useMemo } from 'react';

import { Button } from '../Button';
import { PopupBuilderProductContentSection } from '../contentTypes';
import { Div, P } from '../HTMLElement';
import { t } from '../i18n/i18n';
import { Image } from '../Image';
import { TextAlign } from '../interfaces';
import { useModalContext } from '../ModalContext';
import { Link } from '../router/Link';
import { useProductById } from '../useProductById';
import { buildSpacingClassNamesFromContent, buildVisibilityClassNamesFromContent } from '../util';
import { PropsWithCustomComponent, VariantsController } from '../VariantsController';

export interface PopupBuilderProductComponentProps extends PopupBuilderProductContentSection {
  productId: string;
}

const PopupBuilderProductComponent = ({ options, productId }: PopupBuilderProductComponentProps) => {
  const { close: closeModal } = useModalContext();
  const product = useProductById(productId, { populate: { breadcrumbs: true } });
  const alignment = options?.alignment as TextAlign | undefined;
  const margin = options?.margin && buildSpacingClassNamesFromContent('m', options.margin);
  const visibility = options?.visibility;
  const visibilityClassNames = visibility && buildVisibilityClassNamesFromContent(visibility);

  // Per mostrare i prodotti simili uso il breadcrumb se c'è oppure faccio una ricerca con il nome del prodotto
  const categoryUrl = useMemo(() => {
    if (product?.breadcrumbs) {
      const breadcrumb = product.breadcrumbs.find((bc) => bc.length >= 3);
      if (breadcrumb) {
        const categoryPath = breadcrumb[breadcrumb.length - 2].fullPath;
        if (categoryPath) {
          return categoryPath;
        }
      }
    }

    if (product?.name) {
      return `/search?${qs.stringify({ simpleSearch: product.name }, { skipNulls: true })}`;
    }

    return undefined;
  }, [product?.breadcrumbs, product?.name]);

  return (
    <Div
      alignItems={alignment}
      className={classnames('popup-builder-product-component', options?.classes)}
      display="flex"
      flexDirection="column"
      gap={3}
      textAlign={alignment}
      uiClassName={{ bs5: classnames(margin, visibilityClassNames) }}
    >
      {product?.name && (
        <P fontWeight={600} marginBottom={0} marginX={2} style={{ textWrap: 'balance' }}>
          {product.name}
        </P>
      )}
      {product?.image?.src && (
        <Div background="white" uiClassName={{ bs5: 'w-100' }}>
          <Image alt={product.name} fluid src={product.image.src} width={200} />
        </Div>
      )}
      {product?.url && (
        <Link onClick={closeModal} to={product.url}>
          <Button variant="primary">{t('misc.buy')}</Button>
        </Link>
      )}
      {categoryUrl && (
        <>
          <P margin={0}>{t('misc.or')}</P>
          <Link onClick={closeModal} style={{ color: 'inherit' }} to={categoryUrl}>
            {t('misc.discoverSimilarProducts')}
          </Link>
        </>
      )}
    </Div>
  );
};

export type PopupBuilderProductComponentVariants = '';

const PopupBuilderProductComponentController = (props: PropsWithCustomComponent<PopupBuilderProductComponentProps>) => (
  <VariantsController<PopupBuilderProductComponentProps, PopupBuilderProductComponentVariants>
    {...props}
    variantsControllerConfig={{
      defaultComponent: PopupBuilderProductComponent,
      name: 'PopupBuilderProductComponent',
    }}
  />
);
export { PopupBuilderProductComponentController as PopupBuilderProductComponent };
